/**
 * vCard 3.0 Generator
 * Converts card form data into a vCard string for QR code encoding
 */

export interface VCardData {
  // Compatible vCard fields
  firstName?: string;
  lastName?: string;
  dateOfBirth?: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  country?: string;
  organization?: string;
  institution?: string;
  title?: string;
  
  // Non-compatible fields (will be added to NOTE)
  idNumber?: string;
  licenseNumber?: string;
  studentId?: string;
  pressId?: string;
  issueDate?: string;
  expiryDate?: string;
  nationality?: string;
  height?: string;
  eyeColor?: string;
  hairColor?: string;
  licenseClass?: string;
  licenseType?: string;
  major?: string;
  department?: string;
  boatType?: string;
  fishingType?: string;
  restrictions?: string;
  sex?: string;
  bloodType?: string;
  placeOfBirth?: string;
}

/**
 * Parse date formats to vCard format (YYYYMMDD)
 * Supports: DD.MM.YYYY, DD/MM/YYYY, YYYY-MM-DD
 */
function parseDateToVCard(dateStr: string): string | null {
  if (!dateStr) return null;
  
  // Try DD.MM.YYYY or DD/MM/YYYY format (European/International)
  const europeanMatch = dateStr.match(/^(\d{2})[\.\/](\d{2})[\.\/](\d{4})$/);
  if (europeanMatch) {
    return `${europeanMatch[3]}${europeanMatch[2]}${europeanMatch[1]}`;
  }
  
  // Try YYYY-MM-DD format (ISO)
  const isoMatch = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoMatch) {
    return `${isoMatch[1]}${isoMatch[2]}${isoMatch[3]}`;
  }
  
  return null;
}

/**
 * Escape special characters in vCard values
 */
function escapeVCardValue(value: string): string {
  return value
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\n/g, '\\n');
}

/**
 * Map form data keys to VCardData keys
 */
function mapFormDataToVCard(formData: Record<string, string>): VCardData {
  const keyMappings: Record<string, keyof VCardData> = {
    // First Name
    'first_name': 'firstName',
    'first-name': 'firstName',
    'firstname': 'firstName',
    'firstName': 'firstName',
    'vorname': 'firstName',
    
    // Last Name
    'last_name': 'lastName',
    'last-name': 'lastName',
    'lastname': 'lastName',
    'lastName': 'lastName',
    'nachname': 'lastName',
    'familienname': 'lastName',
    'name': 'lastName',
    
    // Date of Birth
    'date_of_birth': 'dateOfBirth',
    'date-of-birth': 'dateOfBirth',
    'dateofbirth': 'dateOfBirth',
    'dateOfBirth': 'dateOfBirth',
    'dob': 'dateOfBirth',
    'birthday': 'dateOfBirth',
    'birth_date': 'dateOfBirth',
    'birthDate': 'dateOfBirth',
    'geburtsdatum': 'dateOfBirth',
    
    // Address
    'address': 'address',
    'street': 'address',
    'strasse': 'address',
    'adresse': 'address',
    
    // City
    'city': 'city',
    'stadt': 'city',
    'ort': 'city',
    
    // State
    'state': 'state',
    'bundesland': 'state',
    
    // Zip Code
    'zip': 'zipCode',
    'zip_code': 'zipCode',
    'zip-code': 'zipCode',
    'zipcode': 'zipCode',
    'zipCode': 'zipCode',
    'postal_code': 'zipCode',
    'postalCode': 'zipCode',
    'plz': 'zipCode',
    
    // Country
    'country': 'country',
    'land': 'country',
    
    // Organization
    'organization': 'organization',
    'org': 'organization',
    'company': 'organization',
    'firma': 'organization',
    
    // Institution
    'institution': 'institution',
    'school': 'institution',
    'university': 'institution',
    'schule': 'institution',
    
    // Title
    'title': 'title',
    'titel': 'title',
    'position': 'title',
    
    // ID Number
    'id_number': 'idNumber',
    'id-number': 'idNumber',
    'idnumber': 'idNumber',
    'idNumber': 'idNumber',
    'ausweisnummer': 'idNumber',
    
    // License Number
    'license_number': 'licenseNumber',
    'license-number': 'licenseNumber',
    'licensenumber': 'licenseNumber',
    'licenseNumber': 'licenseNumber',
    'führerscheinnummer': 'licenseNumber',
    
    // Student ID
    'student_id': 'studentId',
    'student-id': 'studentId',
    'studentid': 'studentId',
    'studentId': 'studentId',
    'matrikelnummer': 'studentId',
    
    // Press ID
    'press_id': 'pressId',
    'press-id': 'pressId',
    'pressid': 'pressId',
    'pressId': 'pressId',
    
    // Issue Date
    'issue_date': 'issueDate',
    'issue-date': 'issueDate',
    'issuedate': 'issueDate',
    'issueDate': 'issueDate',
    'ausstellungsdatum': 'issueDate',
    
    // Expiry Date
    'expiry_date': 'expiryDate',
    'expiry-date': 'expiryDate',
    'expirydate': 'expiryDate',
    'expiryDate': 'expiryDate',
    'expiration_date': 'expiryDate',
    'expirationDate': 'expiryDate',
    'expires': 'expiryDate',
    'gültig_bis': 'expiryDate',
    
    // Nationality
    'nationality': 'nationality',
    'nationalität': 'nationality',
    'staatsangehörigkeit': 'nationality',
    
    // Height
    'height': 'height',
    'größe': 'height',
    'körpergröße': 'height',
    
    // Eye Color
    'eye_color': 'eyeColor',
    'eye-color': 'eyeColor',
    'eyecolor': 'eyeColor',
    'eyeColor': 'eyeColor',
    'augenfarbe': 'eyeColor',
    
    // Hair Color
    'hair_color': 'hairColor',
    'hair-color': 'hairColor',
    'haircolor': 'hairColor',
    'hairColor': 'hairColor',
    'haarfarbe': 'hairColor',
    
    // License Class
    'license_class': 'licenseClass',
    'license-class': 'licenseClass',
    'licenseclass': 'licenseClass',
    'licenseClass': 'licenseClass',
    'class': 'licenseClass',
    'klasse': 'licenseClass',
    
    // License Type
    'license_type': 'licenseType',
    'license-type': 'licenseType',
    'licensetype': 'licenseType',
    'licenseType': 'licenseType',
    'type': 'licenseType',
    
    // Major
    'major': 'major',
    'studiengang': 'major',
    'fachrichtung': 'major',
    
    // Department
    'department': 'department',
    'abteilung': 'department',
    'fachbereich': 'department',
    
    // Boat Type
    'boat_type': 'boatType',
    'boat-type': 'boatType',
    'boattype': 'boatType',
    'boatType': 'boatType',
    
    // Fishing Type
    'fishing_type': 'fishingType',
    'fishing-type': 'fishingType',
    'fishingtype': 'fishingType',
    'fishingType': 'fishingType',
    
    // Restrictions
    'restrictions': 'restrictions',
    'beschränkungen': 'restrictions',
    
    // Sex
    'sex': 'sex',
    'gender': 'sex',
    'geschlecht': 'sex',
    
    // Blood Type
    'blood_type': 'bloodType',
    'blood-type': 'bloodType',
    'bloodtype': 'bloodType',
    'bloodType': 'bloodType',
    'blutgruppe': 'bloodType',
    
    // Place of Birth
    'place_of_birth': 'placeOfBirth',
    'place-of-birth': 'placeOfBirth',
    'placeofbirth': 'placeOfBirth',
    'placeOfBirth': 'placeOfBirth',
    'geburtsort': 'placeOfBirth',
  };
  
  const result: VCardData = {};
  
  for (const [key, value] of Object.entries(formData)) {
    if (!value) continue;
    
    const normalizedKey = key.toLowerCase().replace(/[\s-_]/g, '_');
    const mappedKey = keyMappings[normalizedKey] || keyMappings[key.toLowerCase()];
    
    if (mappedKey) {
      result[mappedKey] = value;
    }
  }
  
  return result;
}

/**
 * Generate a vCard 3.0 string from form data
 * @param formData - Key-value pairs of form fields
 * @param templateName - Optional template name to use as ORG fallback (e.g., "State ID Card")
 * @param photoUrl - Optional URL to the customer's photo (stored in Supabase Storage)
 */
export function generateVCard(
  formData: Record<string, string>, 
  templateName?: string,
  photoUrl?: string
): string {
  const data = mapFormDataToVCard(formData);
  
  const lines: string[] = [
    'BEGIN:VCARD',
    'VERSION:3.0',
  ];
  
  // Full Name (FN) - required in vCard 3.0
  const fullName = [data.firstName, data.lastName].filter(Boolean).join(' ').trim();
  if (fullName) {
    lines.push(`FN:${escapeVCardValue(fullName)}`);
  } else {
    lines.push('FN:Unknown');
  }
  
  // Structured Name (N)
  const lastName = data.lastName || '';
  const firstName = data.firstName || '';
  lines.push(`N:${escapeVCardValue(lastName)};${escapeVCardValue(firstName)};;;`);
  
  // Birthday (BDAY)
  const bday = parseDateToVCard(data.dateOfBirth || '');
  if (bday) {
    lines.push(`BDAY:${bday}`);
  }
  
  // Address (ADR)
  const hasAddress = data.address || data.city || data.state || data.zipCode || data.country;
  if (hasAddress) {
    const adr = [
      '', // PO Box
      '', // Extended address
      escapeVCardValue(data.address || ''),
      escapeVCardValue(data.city || ''),
      escapeVCardValue(data.state || ''),
      escapeVCardValue(data.zipCode || ''),
      escapeVCardValue(data.country || ''),
    ].join(';');
    lines.push(`ADR;TYPE=HOME:${adr}`);
  }
  
  // Organization (ORG) - use explicit org, institution, or fall back to template name
  const org = data.organization || data.institution || templateName || 'ID Card';
  lines.push(`ORG:${escapeVCardValue(org)}`);
  
  // Title
  if (data.title) {
    lines.push(`TITLE:${escapeVCardValue(data.title)}`);
  }
  
  // Photo URL (from Supabase Storage)
  if (photoUrl) {
    lines.push(`PHOTO;VALUE=uri:${photoUrl}`);
  }
  
  // NOTE - All non-compatible fields
  const noteItems: string[] = [];
  
  if (data.idNumber) noteItems.push(`ID: ${data.idNumber}`);
  if (data.licenseNumber) noteItems.push(`License: ${data.licenseNumber}`);
  if (data.studentId) noteItems.push(`Student ID: ${data.studentId}`);
  if (data.pressId) noteItems.push(`Press ID: ${data.pressId}`);
  if (data.issueDate) noteItems.push(`Issued: ${data.issueDate}`);
  if (data.expiryDate) noteItems.push(`Expires: ${data.expiryDate}`);
  if (data.nationality) noteItems.push(`Nationality: ${data.nationality}`);
  if (data.placeOfBirth) noteItems.push(`Place of Birth: ${data.placeOfBirth}`);
  if (data.height) noteItems.push(`Height: ${data.height}`);
  if (data.eyeColor) noteItems.push(`Eye Color: ${data.eyeColor}`);
  if (data.hairColor) noteItems.push(`Hair Color: ${data.hairColor}`);
  if (data.sex) noteItems.push(`Sex: ${data.sex}`);
  if (data.bloodType) noteItems.push(`Blood Type: ${data.bloodType}`);
  if (data.licenseClass) noteItems.push(`Class: ${data.licenseClass}`);
  if (data.licenseType) noteItems.push(`Type: ${data.licenseType}`);
  if (data.major) noteItems.push(`Major: ${data.major}`);
  if (data.department) noteItems.push(`Department: ${data.department}`);
  if (data.boatType) noteItems.push(`Boat Type: ${data.boatType}`);
  if (data.fishingType) noteItems.push(`Fishing Type: ${data.fishingType}`);
  if (data.restrictions) noteItems.push(`Restrictions: ${data.restrictions}`);
  
  if (noteItems.length > 0) {
    lines.push(`NOTE:${escapeVCardValue(noteItems.join(' | '))}`);
  }
  
  lines.push('END:VCARD');
  
  return lines.join('\r\n');
}
